/**
 * Command profiles (ADR-0117): what the Command Centre gives a person on duty, set per
 * organisation (control plane `GET/PUT /v1/tenants/{tenant}/command-profiles`, audited). A
 * profile is for people holding some roles and lists their work lists ("My shift"), the kinds of
 * record they look up (a person, an order) with what to show next to it, and the actions they may
 * start. Nothing here is specific to one organisation: police duty officers and bank payment
 * teams are both just configurations.
 *
 * Every lookup and every action runs through an agent (a request with the same rules, approvals
 * and records as anywhere else): a profile never calls a system directly.
 */
import type { Http } from "./http";
import type { InboxItem, CaseOut } from "./types";
import type { EntityRef } from "./viewData";
import { SECTION_KINDS, sourceValid, type RecordWorkspace } from "./recordWorkspace";
import moiExample from "./fixtures/commandProfiles/moi-duty-officer.json";
import bankExample from "./fixtures/commandProfiles/bank-payments.json";

/** Two example settings (police duty officers, bank payment operations): one schema, any organisation. */
export const COMMAND_PROFILE_EXAMPLES: Record<"police" | "bank", CommandProfilesDoc> = { police: moiExample as unknown as CommandProfilesDoc, bank: bankExample as unknown as CommandProfilesDoc };

export type Bi = { en: string; ar: string };

/** A system call made by an agent: `tool.action` with arguments (placeholders allowed). */
export interface ToolCallRef {
  tool: string;
  action: string;
  args?: Record<string, unknown>;
}

export interface WorkList {
  id: string;
  label: Bi;
  source: "inbox" | "cases" | "tool";
  /** source "tool": the call to list the items (run by `agent`). */
  call?: ToolCallRef;
  /** inbox: `queue`, `kind`; cases: `status`, `queue`, `type`; tool: field = value on the items. */
  filter?: Record<string, string>;
  /** The agent a click on an item starts (and, for "tool", the one that lists them). */
  agent?: string;
  /** Agent input from the item: `{ "order_no": "{item.order_no}" }`. */
  input?: Record<string, string>;
  /** What a click does: start the agent (default), open the item's record card, or open the task. */
  open?: "agent" | "record" | "task";
  /** Record type of the items (for `open: "record"`), and the item field holding its key. */
  record?: { type: string; field: string };
  /** How an item reads: `{order_no} · {person_name}`. */
  item_label?: string;
}

export interface RelatedCall extends ToolCallRef {
  /** The argument the record's key goes into. */
  arg: string;
  label: Bi;
  agent?: string;
}

export interface RecordType {
  type: string;
  label: Bi;
  /** The field that identifies a record (`emirates_id`, `order_no`). */
  key: string;
  /** `question`: what the lookup asks the agent in words (`{record.key}` filled), else `<action> <key>`. */
  lookup: { tool: string; action: string; arg: string; agent?: string; args?: Record<string, unknown>; question?: Bi };
  related?: RelatedCall[];
  /** Ids of the profile's actions offered on this record. */
  actions?: string[];
  /** Ask why the person opens a record of this type (once per record per session); audited (ADR-0118). */
  purpose_required?: boolean;
  /** The record workspace (`/records/<type>/<key>`): its sections and analyses (ADR-0118). */
  workspace?: RecordWorkspace | null;
}

export interface ProfileAction {
  id: string;
  label: Bi;
  /** The agent that does it; or a system call a profile agent is asked to make. */
  agent?: string;
  call?: { tool: string; action: string };
  input?: Record<string, string>;
  /** Only people holding one of these roles see it (none: everyone with the profile). */
  roles?: string[];
}

export interface CommandProfile {
  id: string;
  label: Bi;
  roles: string[];
  default_agent?: string | null;
  work_lists: WorkList[];
  record_types: RecordType[];
  actions: ProfileAction[];
  /** Live alerts: notifications for these roles (default: the profile's roles) slide in. */
  alerts?: { roles?: string[] } | null;
}

export interface CommandProfilesDoc {
  profiles: CommandProfile[];
}

export interface CommandProfilesOut extends CommandProfilesDoc {
  tenant: string;
  updated_by: string | null;
  updated_at: string | null;
}

export const PROFILE_ID = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const AGENT = /^[a-z][a-z0-9-]{2,40}$/;
const ROLE = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,63}$/;
const TOOL = /^[a-z][a-z0-9_.-]{0,62}$/;
const FIELD = /^[A-Za-z_][A-Za-z0-9_.]{0,63}$/;

// ------------------------------------------------------------------------------- checks

/** Client-side checks (i18n keys `officer:issues.<code>`); the control plane checks again. */
export function profileIssues(doc: CommandProfilesDoc): { path: string; code: string }[] {
  const out: { path: string; code: string }[] = [];
  const bad = (path: string, code: string) => out.push({ path, code });
  const label = (l: Bi | undefined, path: string) => {
    if (!l || (!l.en?.trim() && !l.ar?.trim())) bad(path, "label_required");
  };
  const ids = new Set<string>();
  doc.profiles.forEach((p, i) => {
    const at = `profiles.${i}`;
    if (!PROFILE_ID.test(p.id ?? "")) bad(`${at}.id`, "id_invalid");
    if (ids.has(p.id)) bad(`${at}.id`, "id_duplicate");
    ids.add(p.id);
    label(p.label, `${at}.label`);
    if (!p.roles?.length) bad(`${at}.roles`, "roles_required");
    if (p.roles?.some((r) => !ROLE.test(r))) bad(`${at}.roles`, "role_invalid");
    if (p.default_agent && !AGENT.test(p.default_agent)) bad(`${at}.default_agent`, "agent_invalid");
    const actionIds = new Set(p.actions.map((a) => a.id));
    const listIds = new Set<string>();
    p.work_lists.forEach((w, j) => {
      const wa = `${at}.work_lists.${j}`;
      if (!PROFILE_ID.test(w.id ?? "") || listIds.has(w.id)) bad(`${wa}.id`, "id_invalid");
      listIds.add(w.id);
      label(w.label, `${wa}.label`);
      if (!["inbox", "cases", "tool"].includes(w.source)) bad(`${wa}.source`, "source_invalid");
      if (w.source === "tool" && (!w.call || !TOOL.test(w.call.tool) || !TOOL.test(w.call.action))) bad(`${wa}.call`, "call_required");
      if (w.source === "tool" && !(w.agent || p.default_agent)) bad(`${wa}.agent`, "agent_required");
      if (w.agent && !AGENT.test(w.agent)) bad(`${wa}.agent`, "agent_invalid");
      if (w.open === "record" && !w.record) bad(`${wa}.record`, "record_required");
    });
    const types = new Set<string>();
    p.record_types.forEach((r, j) => {
      const ra = `${at}.record_types.${j}`;
      if (!PROFILE_ID.test(r.type ?? "") || types.has(r.type)) bad(`${ra}.type`, "id_invalid");
      types.add(r.type);
      label(r.label, `${ra}.label`);
      if (!FIELD.test(r.key ?? "")) bad(`${ra}.key`, "field_invalid");
      if (!r.lookup || !TOOL.test(r.lookup.tool ?? "") || !TOOL.test(r.lookup.action ?? "") || !FIELD.test(r.lookup.arg ?? "")) bad(`${ra}.lookup`, "call_required");
      if (!(r.lookup?.agent || p.default_agent)) bad(`${ra}.lookup`, "agent_required");
      (r.related ?? []).forEach((x, k) => {
        if (!TOOL.test(x.tool ?? "") || !TOOL.test(x.action ?? "") || !FIELD.test(x.arg ?? "")) bad(`${ra}.related.${k}`, "call_required");
        label(x.label, `${ra}.related.${k}.label`);
      });
      for (const a of r.actions ?? []) if (!actionIds.has(a)) bad(`${ra}.actions`, "action_unknown");
      const sectionIds = new Set<string>();
      (r.workspace?.sections ?? []).forEach((s, k) => {
        const sa = `${ra}.workspace.sections.${k}`;
        if (!PROFILE_ID.test(s.id ?? "") || sectionIds.has(s.id)) bad(`${sa}.id`, "id_invalid");
        sectionIds.add(s.id);
        label(s.label, `${sa}.label`);
        if (!sourceValid(s.source ?? "", r)) bad(`${sa}.source`, "section_source");
        if (s.kind && !SECTION_KINDS.includes(s.kind)) bad(`${sa}.kind`, "section_kind");
        if (s.open && (!PROFILE_ID.test(s.open.type ?? "") || !FIELD.test(s.open.field ?? ""))) bad(`${sa}.open`, "section_open");
        if (s.document && (!TOOL.test(s.document.tool ?? "") || !TOOL.test(s.document.action ?? "") || !FIELD.test(s.document.arg ?? ""))) bad(`${sa}.document`, "call_required");
        if (s.document && !(s.document.agent || r.lookup?.agent || p.default_agent)) bad(`${sa}.document`, "agent_required");
      });
      const analysisIds = new Set<string>();
      (r.workspace?.analyses ?? []).forEach((a, k) => {
        const aa = `${ra}.workspace.analyses.${k}`;
        if (!PROFILE_ID.test(a.id ?? "") || analysisIds.has(a.id)) bad(`${aa}.id`, "id_invalid");
        analysisIds.add(a.id);
        label(a.label, `${aa}.label`);
        if (!AGENT.test(a.agent ?? "")) bad(`${aa}.agent`, "agent_invalid");
      });
    });
    const seen = new Set<string>();
    p.actions.forEach((a, j) => {
      const aa = `${at}.actions.${j}`;
      if (!PROFILE_ID.test(a.id ?? "") || seen.has(a.id)) bad(`${aa}.id`, "id_invalid");
      seen.add(a.id);
      label(a.label, `${aa}.label`);
      if (!a.agent && !a.call) bad(`${aa}`, "action_target");
      if (a.call && !(a.agent || p.default_agent)) bad(`${aa}`, "agent_required");
      if (a.agent && !AGENT.test(a.agent)) bad(`${aa}.agent`, "agent_invalid");
    });
  });
  return out;
}

/** Fills in what a hand-written profile may leave out, so the screens never meet `undefined`. */
export function normaliseProfile(p: Partial<CommandProfile> & { id: string }): CommandProfile {
  return {
    id: p.id,
    label: p.label ?? { en: p.id, ar: p.id },
    roles: p.roles ?? [],
    default_agent: p.default_agent ?? null,
    work_lists: p.work_lists ?? [],
    record_types: p.record_types ?? [],
    actions: p.actions ?? [],
    alerts: p.alerts ?? null,
  };
}

// ---------------------------------------------------------------------------- resolution

/** The profiles a person gets: those whose roles they hold, in the organisation's order. */
export function profilesFor(doc: CommandProfilesDoc | null | undefined, roles: readonly string[]): CommandProfile[] {
  const mine = new Set(roles);
  return (doc?.profiles ?? []).map(normaliseProfile).filter((p) => p.roles.some((r) => mine.has(r)));
}

/** The profile in use: the one chosen before (when still allowed), else the first. */
export function activeProfile(list: CommandProfile[], chosen: string | null): CommandProfile | null {
  return list.find((p) => p.id === chosen) ?? list[0] ?? null;
}

export interface PlaceholderContext {
  user?: Record<string, unknown>;
  item?: Record<string, unknown>;
  record?: Record<string, unknown>;
  [scope: string]: Record<string, unknown> | undefined;
}

const PLACEHOLDER = /\{([a-z_]+)\.([A-Za-z0-9_.]+)\}/g;

function lookup(ctx: PlaceholderContext, scope: string, path: string): unknown {
  return path.split(".").reduce<unknown>((n, k) => (n && typeof n === "object" ? (n as Record<string, unknown>)[k] : undefined), ctx[scope]);
}

/**
 * `{user.unit}`, `{item.order_no}`, `{record.key}` filled from the context. A value that is the
 * placeholder alone keeps its type (a number stays a number); unknown placeholders become empty.
 */
export function fillPlaceholders<T>(template: T, ctx: PlaceholderContext): T {
  if (typeof template === "string") {
    const whole = /^\{([a-z_]+)\.([A-Za-z0-9_.]+)\}$/.exec(template);
    if (whole) {
      const v = lookup(ctx, whole[1] as string, whole[2] as string);
      return (v === undefined || v === null ? "" : v) as T;
    }
    return template.replace(PLACEHOLDER, (_, scope: string, path: string) => {
      const v = lookup(ctx, scope, path);
      return v === undefined || v === null ? "" : String(v);
    }) as T;
  }
  if (Array.isArray(template)) return template.map((x) => fillPlaceholders(x, ctx)) as T;
  if (template && typeof template === "object") return Object.fromEntries(Object.entries(template).map(([k, v]) => [k, fillPlaceholders(v, ctx)])) as T;
  return template;
}

/** Placeholders a template still needs from this context (shown before anything starts). */
export function missingPlaceholders(template: unknown, ctx: PlaceholderContext): string[] {
  const out = new Set<string>();
  const walk = (v: unknown) => {
    if (typeof v === "string") {
      for (const m of v.matchAll(PLACEHOLDER)) if (lookup(ctx, m[1] as string, m[2] as string) === undefined) out.add(`${m[1]}.${m[2]}`);
    } else if (v && typeof v === "object") Object.values(v).forEach(walk);
  };
  walk(template);
  return [...out];
}

/** The person as placeholders see them: id, name, tenant, roles and token attributes (`unit`). */
export function userContext(u: { sub: string; username: string; name: string; tenant: string; roles: string[]; attributes?: Record<string, string> } | null): Record<string, unknown> {
  if (!u) return {};
  return { ...(u.attributes ?? {}), id: u.sub, sub: u.sub, username: u.username, name: u.name, tenant: u.tenant };
}

// ------------------------------------------------------------------------------ work lists

export interface WorkItem {
  key: string;
  label: string;
  /** The item's fields (an inbox task, a case or a row the tool returned). */
  data: Record<string, unknown>;
  taskId?: string;
}

/** An item's label from `{order_no} · {person_name}` (bare fields or `{item.field}`). */
export function itemLabel(template: string, row: Record<string, unknown>): string {
  return template.replace(/\{(?:item\.)?([A-Za-z0-9_.]+)\}/g, (_, path: string) => {
    const v = deep(row, path);
    return v === undefined || v === null ? "" : String(v);
  });
}

const matches = (filter: Record<string, string> | undefined, get: (k: string) => unknown) => Object.entries(filter ?? {}).every(([k, v]) => v === "" || String(get(k) ?? "") === v);
const deep = (o: unknown, path: string): unknown => path.split(".").reduce<unknown>((n, k) => (n && typeof n === "object" ? (n as Record<string, unknown>)[k] : undefined), o);

/** Inbox tasks of a work list (the inbox already holds only what the person may decide). */
export function inboxItems(list: WorkList, inbox: InboxItem[], lang: "ar" | "en"): WorkItem[] {
  return inbox
    .filter((i) => matches(list.filter, (k) => (k === "queue" ? i.task.queue : k === "kind" ? i.task.kind : k === "type" ? i.case?.type : deep(i.task, k))))
    .map((i) => ({ key: i.task.id, taskId: i.task.id, label: (lang === "en" ? i.task.title.en : i.task.title.ar) || i.task.title.en || i.task.title.ar || "", data: { ...i.task, case: i.case } as unknown as Record<string, unknown> }));
}

export function caseItems(list: WorkList, cases: CaseOut[], lang: "ar" | "en"): WorkItem[] {
  return cases
    .filter((c) => matches(list.filter, (k) => deep(c, k) ?? deep(c.attributes, k)))
    .map((c) => ({ key: c.id, label: list.item_label ? itemLabel(list.item_label, { ...c.attributes, ...c } as Record<string, unknown>) : (lang === "en" ? c.title.en : c.title.ar) || c.title.en || "", data: { ...c.attributes, ...c } as unknown as Record<string, unknown> }));
}

/** Rows a tool returned for a work list, filtered and labelled. */
export function toolItems(list: WorkList, rows: Record<string, unknown>[]): WorkItem[] {
  return rows
    .filter((r) => matches(list.filter, (k) => deep(r, k)))
    .map((r, i) => {
      const key = list.record ? String(deep(r, list.record.field) ?? i) : String(r.id ?? r.key ?? i);
      const label = list.item_label ? itemLabel(list.item_label, r) : String(r.title ?? r.name ?? r.label ?? key);
      return { key, label, data: r };
    });
}

/** What a click on a work list item does. */
export type ItemOpen = { kind: "task"; taskId: string } | { kind: "record"; entity: EntityRef } | { kind: "agent"; agent: string; input: Record<string, unknown>; question: string } | { kind: "none" };

export function openItem(list: WorkList, profile: CommandProfile, item: WorkItem, user: Record<string, unknown>): ItemOpen {
  const mode = list.open ?? (list.source === "inbox" && !list.agent ? "task" : "agent");
  if (mode === "task" && item.taskId) return { kind: "task", taskId: item.taskId };
  if (mode === "record" && list.record) {
    const key = deep(item.data, list.record.field);
    return key === undefined || key === null || key === "" ? { kind: "none" } : { kind: "record", entity: { type: list.record.type, key: String(key) } };
  }
  const agent = list.agent ?? profile.default_agent ?? null;
  if (!agent) return item.taskId ? { kind: "task", taskId: item.taskId } : { kind: "none" };
  const input = fillPlaceholders(list.input ?? {}, { user, item: item.data });
  return { kind: "agent", agent, input, question: item.label };
}

// ---------------------------------------------------------------------- actions and records

export function recordType(profile: CommandProfile | null, type: string): RecordType | undefined {
  return profile?.record_types.find((r) => r.type === type);
}

export function actionAllowed(a: ProfileAction, roles: readonly string[]): boolean {
  return !a.roles?.length || a.roles.some((r) => roles.includes(r));
}

/** The actions of a record type the person may start. */
export function recordActions(profile: CommandProfile, type: string, roles: readonly string[]): ProfileAction[] {
  const ids = recordType(profile, type)?.actions ?? [];
  return ids.map((id) => profile.actions.find((a) => a.id === id)).filter((a): a is ProfileAction => !!a && actionAllowed(a, roles));
}

export interface Suggestion {
  action: ProfileAction;
  entity: EntityRef;
}

/** Profile actions for the records an answer mentions (at most `max`, first records first). */
export function suggestedActions(profile: CommandProfile | null, entities: EntityRef[], roles: readonly string[], max = 4): Suggestion[] {
  if (!profile) return [];
  const out: Suggestion[] = [];
  for (const e of entities) {
    for (const a of recordActions(profile, e.type, roles)) {
      if (out.length >= max) return out;
      if (!out.some((s) => s.action.id === a.id && s.entity.key === e.key)) out.push({ action: a, entity: e });
    }
  }
  return out;
}

/** The request an action starts: its agent (or the profile's, asked to make the call). */
export function actionRequest(profile: CommandProfile, a: ProfileAction, ctx: PlaceholderContext, lang: "ar" | "en"): { agent: string; input: Record<string, unknown>; question: string } | null {
  const agent = a.agent ?? profile.default_agent ?? null;
  if (!agent) return null;
  const input = fillPlaceholders(a.input ?? {}, ctx);
  const about = ctx.record && typeof ctx.record.key === "string" ? ` ${ctx.record.key}` : "";
  const question = `${(lang === "en" ? a.label.en : a.label.ar) || a.label.en}${about}`;
  return { agent, input: a.call ? { ...input, requested_call: { tool: a.call.tool, action: a.call.action, args: input } } : input, question };
}

/** The agent request that runs one lookup (the record's or a related call) for a key. */
export function lookupRequest(profile: CommandProfile, call: { tool: string; action: string; arg: string; agent?: string; args?: Record<string, unknown>; question?: Bi }, key: string, user: Record<string, unknown>, lang: "ar" | "en" = "en"): { agent: string; input: Record<string, unknown>; question: string } | null {
  const agent = call.agent ?? profile.default_agent ?? null;
  if (!agent) return null;
  const args = { ...compact(fillPlaceholders(call.args ?? {}, { user, record: { key } })), [call.arg]: key };
  const words = call.question ? fillPlaceholders((lang === "en" ? call.question.en : call.question.ar) || call.question.en, { user, record: { key } }) : "";
  return { agent, input: { ...args, lookup: { tool: call.tool, action: call.action, args } }, question: words.trim() || `${call.action} ${key}` };
}

/** Arguments whose placeholder had no value are left out (an empty station would filter everything away). */
function compact(args: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(args).filter(([, v]) => v !== "" && v !== null && v !== undefined));
}

/** The request that lists a tool work list's items. */
export function listRequest(profile: CommandProfile, list: WorkList, user: Record<string, unknown>): { agent: string; input: Record<string, unknown>; question: string } | null {
  if (list.source !== "tool" || !list.call) return null;
  const agent = list.agent ?? profile.default_agent ?? null;
  if (!agent) return null;
  const args = compact(fillPlaceholders(list.call.args ?? {}, { user }));
  return { agent, input: { ...args, lookup: { tool: list.call.tool, action: list.call.action, args } }, question: `${list.call.action}` };
}

// ------------------------------------------------------------------------------------- api

export function commandProfilesApi(http: Http) {
  const enc = encodeURIComponent;
  return {
    get: (tenant: string) => http.get<CommandProfilesOut>("controlPlane", `/v1/tenants/${enc(tenant)}/command-profiles`),
    put: (tenant: string, doc: CommandProfilesDoc & { reason?: string }) => http.put<CommandProfilesOut>("controlPlane", `/v1/tenants/${enc(tenant)}/command-profiles`, doc),
  };
}
